import React, { Fragment, useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import { fetchAccountingSettings, updateAccountingSettings } from "../../../api/accounting";
import { formatApiError, runCrud, runLoad, runRefresh } from "../Common/credoraToast";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";

const parseBoolSetting = (value) => String(value).toLowerCase() === "true";

const AccountingSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    fiscal_year_start_month: "1",
    default_cash_account_code: "1000",
    default_ap_account_code: "2000",
    require_expense_approval: true,
    auto_post_expense_on_payment: true,
    journal_entry_prefix: "JE",
  });

  const applySettings = useCallback((rows) => {
    const map = Object.fromEntries((rows || []).map((s) => [s.key, s.value]));
    setForm({
      fiscal_year_start_month: map.fiscal_year_start_month || "1",
      default_cash_account_code: map.default_cash_account_code || "1000",
      default_ap_account_code: map.default_ap_account_code || "2000",
      require_expense_approval: parseBoolSetting(map.require_expense_approval ?? "true"),
      auto_post_expense_on_payment: parseBoolSetting(map.auto_post_expense_on_payment ?? "true"),
      journal_entry_prefix: map.journal_entry_prefix || "JE",
    });
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    return runLoad(() => fetchAccountingSettings().then(applySettings), {
      errorMessage: "Failed to load accounting settings.",
    })
      .catch((err) => setError(formatApiError(err, "Failed to load accounting settings.")))
      .finally(() => setLoading(false));
  }, [applySettings]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefresh = () =>
    runRefresh(() => fetchAccountingSettings().then(applySettings), { label: "Accounting settings" })
      .catch((err) => setError(formatApiError(err, "Failed to load accounting settings.")));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await runCrud(
        () =>
          updateAccountingSettings({
            fiscal_year_start_month: Number(form.fiscal_year_start_month),
            default_cash_account_code: form.default_cash_account_code,
            default_ap_account_code: form.default_ap_account_code,
            require_expense_approval: form.require_expense_approval,
            auto_post_expense_on_payment: form.auto_post_expense_on_payment,
            journal_entry_prefix: form.journal_entry_prefix,
          }),
        { successMessage: "Accounting settings saved." }
      );
      load();
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key) => setForm((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <Fragment>
      <Breadcrumbs mainTitle={ACCOUNTING_SECTION} parent={ACCOUNTING_SECTION} title="Accounting Settings" />
      <div className="container-fluid">
        {error && <Alert color="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <Form onSubmit={handleSave}>
            <div className="card mb-3">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Fiscal calendar & defaults</h5>
                <Button color="light" size="sm" className="border" type="button" onClick={handleRefresh} disabled={saving}>
                  Refresh
                </Button>
              </div>
              <div className="card-body">
                <Row>
                  <Col md="4">
                    <FormGroup>
                      <Label>Fiscal year start month</Label>
                      <Input
                        type="select"
                        value={form.fiscal_year_start_month}
                        onChange={(e) => setForm({ ...form, fiscal_year_start_month: e.target.value })}>
                        {Array.from({ length: 12 }, (_, i) => (
                          <option key={i + 1} value={String(i + 1)}>
                            {new Date(2000, i, 1).toLocaleString("default", { month: "long" })}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Default cash account code</Label>
                      <Input
                        value={form.default_cash_account_code}
                        onChange={(e) => setForm({ ...form, default_cash_account_code: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Default AP account code</Label>
                      <Input
                        value={form.default_ap_account_code}
                        onChange={(e) => setForm({ ...form, default_ap_account_code: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Journal entry prefix</Label>
                      <Input
                        value={form.journal_entry_prefix}
                        onChange={(e) => setForm({ ...form, journal_entry_prefix: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-header">
                <h5 className="mb-0">Expense workflow</h5>
              </div>
              <div className="card-body">
                <FormGroup switch className="mb-3">
                  <Input
                    type="switch"
                    role="switch"
                    checked={form.require_expense_approval}
                    onChange={() => toggle("require_expense_approval")}
                  />
                  <Label check>Require approval before payment</Label>
                </FormGroup>
                <FormGroup switch>
                  <Input
                    type="switch"
                    role="switch"
                    checked={form.auto_post_expense_on_payment}
                    onChange={() => toggle("auto_post_expense_on_payment")}
                  />
                  <Label check>Auto-post journal when expense is paid</Label>
                </FormGroup>
              </div>
            </div>

            <Button color="primary" type="submit" disabled={saving}>
              {saving ? "Saving…" : "Save settings"}
            </Button>
          </Form>
        )}
      </div>
    </Fragment>
  );
};

export default AccountingSettingsPage;
